import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth0 } from 'src/auth/useAuth0.hook';
import { FileTagEnum } from 'src/generated/graphql-types';
import { dispatch } from 'src/redux/config/configureStore';
import { getFilesById } from 'src/redux/slices/fileTagging.service';
import {
  setActiveOperator,
  setCategoryFilter,
  setEntityView,
  setOperatorFilterId,
  setSelectedFund,
  setSelectedOperator,
  setSelectedProject,
  setShouldDisplayOperatorSelection,
} from 'src/redux/slices/investor.slice';
import { setOfferings } from 'src/redux/slices/operator.slice';
import { i18nService } from 'src/services/i18n.service';
import { StatusCategoryFilterEnum } from 'src/types.d';
import { useGraphQLRequest } from 'src/utils/graphQL/useGraphQLRequest.hook';
import LoadingOrError from '../LoadingOrError/LoadingOrError';
import './SelectOperatorComponent.css';

/**
 * The operator box display data, used to display the operator box
 */
export interface OperatorBoxDisplay {
  name: string;
  logoUrl?: string;
  id: string;
  isUnderMaintenance?: boolean;
}

/**
 * The select operator component props
 */
interface SelectOperatorComponentProps {
  operatorsBoxData: OperatorBoxDisplay[];
}

/**
 * The select operator component
 */
export const SelectOperatorComponent: React.FC<SelectOperatorComponentProps> = ({
  operatorsBoxData,
}) => {
  const { getTokenSilently } = useAuth0();
  const { client } = useGraphQLRequest();

  //holds the operator display boxes data
  const [operatorDisplays, setOperatorDisplays] = useState<OperatorBoxDisplay[]>([]);

  const history = useHistory();

  //set the loading state
  const [loading, setLoading] = useState<boolean>(true);

  //update the operator display boxes
  useEffect(() => {
    updateOperatorDisplays();
  }, [operatorsBoxData]);

  useEffect(() => {
    dispatch(setCategoryFilter(StatusCategoryFilterEnum.ALL));
    dispatch(setEntityView({ name: 'All', id: null }));
  }, []);

  /**
   * Handles the click on an operator
   */
  const handleOperatorClick = (operatorId: string) => {
    //validate the operator id
    if (!operatorId) return;

    //update redux with the id and set setShouldDisplayOperatorSelection to false
    dispatch(setOperatorFilterId(operatorId));
    dispatch(setShouldDisplayOperatorSelection(false));

    //render
    dispatch(setSelectedProject(null));
    dispatch(setOfferings(null));
    dispatch(setSelectedFund(null));
    dispatch(setSelectedOperator(null));
    dispatch(setActiveOperator(null));

    //navigate to the overview page
    history.push('/overview');
  };

  /**
   * Updates the operator display boxes
   */
  const updateOperatorDisplays = async () => {
    setLoading(true);
    const token = await getTokenSilently();
    const operatorsDisplay: OperatorBoxDisplay[] = [];

    //validate the operators box data
    if (!operatorsBoxData || operatorsBoxData.length === 0) return;

    //fetch all logos - the backend will return all files related to all operators for this investor
    const allLogos = await getFilesById(token, client, FileTagEnum.Logo, operatorsBoxData[0].id);

    //update the operator display boxes - use for each to make sure we wait for all the files to be fetched
    for (const operator of operatorsBoxData) {
      // const files = await getFilesById(token, client, FileTagEnum.Logo, operator.id);

      const logs = allLogos?.filter(file => file.operatorId === operator.id);

      //fetch the first matching file
      const logoFile = logs?.length > 0 ? await logs[0] : null;

      //add the operator to the operator display boxes
      operatorsDisplay.push({
        ...operator,
        logoUrl: logoFile?.thumbnail || logoFile?.url || '',
      });
    }

    //update the operator display boxes
    setOperatorDisplays(operatorsDisplay?.filter(operator => !operator.isUnderMaintenance) ?? []);
    setLoading(false);
  };

  return (
    <LoadingOrError loading={loading} loadingMessage={i18nService.translate('loadingMessage4')}>
      <div className="operator-page">
        <div className="operator-background">
          <h1 className="operator-title">{i18nService.translate('general.selectOperator')}</h1>
          <div className="operator-container">
            {operatorDisplays.map((operator, index) => (
              <div
                key={index}
                className="operator-card"
                onClick={() => handleOperatorClick(operator.id)}
              >
                <img
                  src={operator.logoUrl}
                  alt={operator.name}
                  className="operator-logo-select-operator"
                />
                <div className="operator-name">{operator.name}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </LoadingOrError>
  );
};
